import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock3,
  LoaderCircle,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { statusLabels } from "../lib/format";
import type { CaseStatus } from "../types/domain";

export const buttonStyles = {
  primary:
    "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-burgundy px-5 py-2.5 font-semibold text-white shadow-sm transition hover:bg-burgundy-dark focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-gold disabled:cursor-not-allowed disabled:opacity-55",
  secondary:
    "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-burgundy/25 bg-white px-5 py-2.5 font-semibold text-burgundy transition hover:border-burgundy hover:bg-ivory focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-gold dark:bg-charcoal dark:text-ivory",
  subtle:
    "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 py-2 font-semibold text-charcoal transition hover:bg-black/5 focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-gold dark:text-ivory dark:hover:bg-white/10",
  danger:
    "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-red-700 px-5 py-2.5 font-semibold text-white transition hover:bg-red-800 focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-gold disabled:opacity-55",
};

export function Button({
  variant = "primary",
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof buttonStyles;
}) {
  return <button className={twMerge(buttonStyles[variant], className)} {...props} />;
}

export function ButtonLink({
  variant = "primary",
  className,
  children,
  ...props
}: LinkProps & {
  variant?: keyof typeof buttonStyles;
  children: ReactNode;
}) {
  return (
    <Link className={twMerge(buttonStyles[variant], className)} {...props}>
      {children}
    </Link>
  );
}

const statusTone: Record<
  CaseStatus,
  "neutral" | "info" | "warning" | "success" | "danger"
> = {
  draft: "neutral",
  submitted: "info",
  initial_review: "info",
  correction_required: "warning",
  correction_submitted: "info",
  admitted: "success",
  assigned: "success",
  in_progress: "info",
  resolved: "success",
  rejected: "danger",
  archived: "neutral",
  cancelled: "neutral",
};

export function StatusBadge({ status }: { status: CaseStatus }) {
  const tone = statusTone[status];
  const Icon =
    tone === "success"
      ? CheckCircle2
      : tone === "danger"
        ? XCircle
        : tone === "warning"
          ? AlertTriangle
          : tone === "info"
            ? Clock3
            : ShieldCheck;
  return (
    <span className={`status-badge status-${tone}`}>
      <Icon aria-hidden="true" size={14} />
      {statusLabels[status]}
    </span>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-heading">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function PageIntro({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string;
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <header className="page-intro">
      <div className="container page-intro-inner">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        {children}
      </div>
    </header>
  );
}

export function LoadingState({ label = "Cargando" }: { label?: string }) {
  return (
    <div className="state-panel" role="status" aria-live="polite">
      <LoaderCircle className="animate-spin" aria-hidden="true" />
      <p>{label}…</p>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="state-panel">
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <div className="alert alert-error" role="alert">
      <AlertTriangle aria-hidden="true" />
      <div>
        <strong>No pudimos completar la acción</strong>
        <p>{message}</p>
      </div>
    </div>
  );
}

export function InlineLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link className="inline-link" to={to}>
      {children}
      <ArrowRight aria-hidden="true" size={16} />
    </Link>
  );
}
